import React, { useState } from "react";
import Image from "next/image";
import { VisuallyHidden } from "@radix-ui/react-visually-hidden";
import {
    Dialog,
    DialogContent,
    DialogTrigger,
    DialogHeader,
    DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { cn } from "@/lib/utils";
import {
    ChevronLeft,
    ChevronRight,
    Expand,
    Star,
    X,
} from "lucide-react";

export interface Product {
    id: string | number;
    name: string;
    category?: string;
    description?: string;
    price: number;
    originalPrice?: number;
    rating?: number;
    images: string[];
    sizes?: string[];
    colors?: string[];
}

export interface ProductDetailsDialogProps {
    product: Product;
    open: boolean;
    onOpenChange: (open: boolean) => void;
    trigger?: React.ReactNode;
    onAddToCart?: (product: Product, size?: string, color?: string) => void;
    formatPrice?: (price: number) => string;
}

const defaultFormatPrice = (price: number) =>
    `₦${(price).toLocaleString()}`;

export const ProductDetailsDialog: React.FC<ProductDetailsDialogProps> = ({
    product,
    open,
    onOpenChange,
    trigger,
    onAddToCart,
    formatPrice = defaultFormatPrice,
}) => {
    const [selectedImageIndex, setSelectedImageIndex] = useState(0);
    const [selectedSize, setSelectedSize] = useState<string | undefined>();
    const [selectedColor, setSelectedColor] = useState<string | undefined>();

    const productImages = product.images?.length
        ? product.images
        : ["/placeholder.svg"];

    const handleAddToCart = () => {
        onAddToCart?.(product, selectedSize, selectedColor);
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogTrigger asChild>
                {trigger ?? (
                    <Button
                        size="sm"
                        variant="secondary"
                        className="w-8 h-8 p-0 bg-white/80 hover:bg-white dark:bg-gray-700/80 dark:hover:bg-gray-700"
                    >
                        <Expand className="h-4 w-4" />
                    </Button>
                )}
            </DialogTrigger>

            <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto p-0">
                <DialogHeader>
                    {/* Hidden for accessibility */}
                    <VisuallyHidden>
                        <DialogTitle>Product Details</DialogTitle>
                    </VisuallyHidden>
                </DialogHeader>

                {/* Sticky Header */}
                <div className="sticky top-0 z-10 bg-white dark:bg-gray-800 p-4 border-b border-gray-200 dark:border-gray-700 flex justify-between items-center">
                    <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                        Product Details
                    </h2>
                    <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => onOpenChange(false)}
                        className="h-8 w-8 p-0"
                    >
                        <X className="h-4 w-4" />
                    </Button>
                </div>

                {/* Body */}
                <div className="p-6">
                    <div className="grid md:grid-cols-2 gap-6">
                        {/* Left – Gallery */}
                        <div className="space-y-4">
                            <div className="relative">
                                <Image
                                    src={productImages[selectedImageIndex]}
                                    alt={product.name}
                                    width={500}
                                    height={600}
                                    className="w-full h-96 object-cover rounded-lg"
                                    priority
                                />
                                {productImages.length > 1 && (
                                    <>
                                        <Button
                                            variant="outline"
                                            size="sm"
                                            className="absolute left-2 top-1/2 transform -translate-y-1/2 bg-white/80 hover:bg-white"
                                            onClick={() =>
                                                setSelectedImageIndex((prev) =>
                                                    prev === 0 ? productImages.length - 1 : prev - 1
                                                )
                                            }
                                        >
                                            <ChevronLeft className="h-4 w-4" />
                                        </Button>
                                        <Button
                                            variant="outline"
                                            size="sm"
                                            className="absolute right-2 top-1/2 transform -translate-y-1/2 bg-white/80 hover:bg-white"
                                            onClick={() =>
                                                setSelectedImageIndex(
                                                    (prev) => (prev + 1) % productImages.length
                                                )
                                            }
                                        >
                                            <ChevronRight className="h-4 w-4" />
                                        </Button>
                                    </>
                                )}
                            </div>

                            {productImages.length > 1 && (
                                <div className="flex gap-2 overflow-x-auto">
                                    {productImages.map((img, index) => (
                                        <button
                                            key={index}
                                            onClick={() => setSelectedImageIndex(index)}
                                            className={cn(
                                                "flex-shrink-0 w-16 h-16 rounded-lg overflow-hidden border-2 transition-colors",
                                                selectedImageIndex === index
                                                    ? "border-orange-500"
                                                    : "border-gray-200 dark:border-gray-700"
                                            )}
                                        >
                                            <Image
                                                src={img}
                                                alt={`${product.name} ${index + 1}`}
                                                width={64}
                                                height={64}
                                                className="w-full h-full object-cover"
                                            />
                                        </button>
                                    ))}
                                </div>
                            )}
                        </div>

                        {/* Right – Details */}
                        <div className="space-y-4">
                            <div>
                                {product.category && (
                                    <p className="text-sm text-gray-600 dark:text-gray-400">
                                        {product.category}
                                    </p>
                                )}
                                <h3 className="text-2xl font-bold text-gray-900 dark:text-white">
                                    {product.name}
                                </h3>
                            </div>

                            <div className="flex items-center gap-2">
                                <span className="text-2xl font-bold text-gray-900 dark:text-white">
                                    {formatPrice(product.price)}
                                </span>
                                {product.originalPrice && (
                                    <span className="text-lg text-gray-500 line-through">
                                        {formatPrice(product.originalPrice)}
                                    </span>
                                )}
                            </div>

                            {product.rating && (
                                <div className="flex items-center gap-1">
                                    <Star className="h-4 w-4 fill-amber-400 text-amber-400" />
                                    <span className="text-sm text-gray-600 dark:text-gray-400">
                                        {product.rating}
                                    </span>
                                </div>
                            )}

                            <p className="text-gray-600 dark:text-gray-400">
                                {product.description ||
                                    "Experience premium quality and style with this carefully crafted piece from our exclusive collection."}
                            </p>

                            {/* Size selection */}
                            {product.sizes?.length ? (
                                <div className="space-y-2">
                                    <Label className="text-sm font-medium">Size</Label>
                                    <div className="flex flex-wrap gap-2">
                                        {product.sizes.map((size) => (
                                            <Button
                                                key={size}
                                                variant={selectedSize === size ? "default" : "outline"}
                                                size="sm"
                                                onClick={() => setSelectedSize(size)}
                                                className={
                                                    selectedSize === size
                                                        ? "bg-gradient-to-r from-[#ea580c] to-[#d97706] hover:from-[#dc2626] hover:to-[#ea580c]"
                                                        : ""
                                                }
                                            >
                                                {size}
                                            </Button>
                                        ))}
                                    </div>
                                </div>
                            ) : null}

                            {/* Colour selection */}
                            {product.colors?.length ? (
                                <div className="space-y-2">
                                    <Label className="text-sm font-medium">Colour</Label>
                                    <div className="flex flex-wrap gap-2">
                                        {product.colors.map((color) => (
                                            <Button
                                                key={color}
                                                variant={selectedColor === color ? "default" : "outline"}
                                                size="sm"
                                                onClick={() => setSelectedColor(color)}
                                                className={
                                                    selectedColor === color
                                                        ? "bg-gradient-to-r from-[#ea580c] to-[#d97706] hover:from-[#dc2626] hover:to-[#ea580c]"
                                                        : ""
                                                }
                                            >
                                                {color}
                                            </Button>
                                        ))}
                                    </div>
                                </div>
                            ) : null}

                            <Button
                                onClick={handleAddToCart}
                                className="w-full bg-gradient-to-r from-[#ea580c] to-[#d97706] hover:from-[#dc2626] hover:to-[#ea580c] text-white"
                            >
                                Add to Cart
                            </Button>
                        </div>
                    </div>
                </div>
            </DialogContent>
        </Dialog>
    );
};

export default ProductDetailsDialog;
